/**
 * The frame every page sits in: the same header, the same gutters, the full
 * width of the window.
 *
 * Pages used to pick their own: `max-w-7xl mx-auto` here, `max-w-4xl` flush
 * left there, `max-w-3xl mx-auto` in a third — so the content jumped around as
 * an editor moved between tools. Now the header comes from the navigation
 * config (title, icon, the tutorial's one-line description) and the body is one
 * of two shapes:
 *
 * - `scroll` (default): a document that scrolls under the header, with the
 *   standard gutters. Most tools.
 * - `workspace`: the area under the header belongs to the page, which fills
 *   it with side panels and scroll regions of its own (Article Generator,
 *   Revenue News, Tutorial). The area is a size container: narrower than
 *   45rem — a phone, a small window, a narrow frame in the Media Manager — its
 *   panels stack into one scroll area, so a page gives its panels their width
 *   and their own scrolling from `@min-[45rem]:` on.
 *
 * Do not cap or centre a page's content (`max-w-*`, `mx-auto`) inside either
 * shape. Constrain a paragraph's measure where it is read, not the page.
 */

import type { ReactNode, Ref } from 'react'

import { navItem, viewDescription, type View } from '../../navigation'

interface PageLayoutProps {
    view: View
    /** Right-aligned header controls — refresh, export, a language switch. */
    actions?: ReactNode
    variant?: 'scroll' | 'workspace'
    /** Replaces the tagline — only for a page whose own text is switchable (the Tutorial's language). */
    description?: string
    /** The scrolling element of a `scroll` page, for pages that reset scroll. */
    scrollRef?: Ref<HTMLDivElement>
    children: ReactNode
}

export default function PageLayout({ view, actions, variant = 'scroll', description, scrollRef, children }: PageLayoutProps) {
    return (
        <div className="flex h-full min-w-0 flex-1 flex-col bg-canvas">
            <PageHeader view={view} actions={actions} description={description} />
            {variant === 'scroll' ? (
                <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
                    <div className="space-y-6 px-6 py-6">{children}</div>
                </div>
            ) : (
                // The page's own width, not the window's: the sidebar and the
                // Media Manager's frame take their share first.
                <div className="@container min-h-0 flex-1">
                    <div className="flex h-full flex-col overflow-y-auto @min-[45rem]:flex-row @min-[45rem]:overflow-hidden">
                        {children}
                    </div>
                </div>
            )}
        </div>
    )
}

export function PageHeader({ view, actions, description: override }: { view: View; actions?: ReactNode; description?: string }) {
    const { label, icon: Icon } = navItem(view)
    const description = override ?? viewDescription(view)
    return (
        <header className="flex min-h-16 shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-6 py-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
            </div>
            {/* The title keeps 12rem; past that, the actions wrap to a second row rather than squeezing it. */}
            <div className="min-w-48 flex-1">
                <h1 className="truncate text-base font-bold leading-tight text-fg-strong">{label}</h1>
                <p className="mt-0.5 truncate text-xs text-fg-muted" title={description}>
                    {description}
                </p>
            </div>
            {actions && <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{actions}</div>}
        </header>
    )
}
